// typed API client — เรียก Go backend (proxy /api ใน dev)

export type Level = "province" | "amphoe" | "tambon";

export interface Indicator {
  indicator_id: number;
  code: string;
  name_th: string;
  name_en?: string;
  unit?: string;
  value_type?: string;
  category?: string;
  subcategory?: string;
  direction?: string;
  decimals: number;
  years?: number[];
  levels?: string[];
}

export interface ChoroplethMeta {
  min: number;
  max: number;
  bins?: number[];
  unit?: string;
  value_type?: string;
  direction?: string;
}

export interface Choropleth {
  meta: ChoroplethMeta;
  values: Record<string, number>;
}

export interface SearchHit {
  area_code: string;
  level: Level;
  name_th: string;
  path: string;
  score: number;
}

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} for ${url}`);
  return (await res.json()) as T;
}

export const api = {
  indicators: () => getJSON<Indicator[]>("/api/indicators"),

  choropleth: (indicator: string, level: Level, period: number, classify = "quantile") =>
    getJSON<Choropleth>(
      `/api/choropleth?indicator=${encodeURIComponent(indicator)}&level=${level}&period=${period}&classify=${classify}`,
    ),

  search: (q: string) => getJSON<SearchHit[]>(`/api/search?q=${encodeURIComponent(q)}`),

  area: (code: string) => getJSON<AreaDetail>(`/api/area/${encodeURIComponent(code)}`),

  ranking: (
    indicator: string,
    level: Level,
    period: number,
    opts: { parent?: string; order?: "asc" | "desc"; limit?: number } = {},
  ) => {
    const p = new URLSearchParams({ indicator, level, period: String(period) });
    if (opts.parent) p.set("parent", opts.parent);
    if (opts.order) p.set("order", opts.order);
    if (opts.limit) p.set("limit", String(opts.limit));
    return getJSON<Ranking>(`/api/ranking?${p.toString()}`);
  },
};

export interface RankItem {
  area_code: string;
  name_th: string;
  parent_code?: string;
  value: number;
  rank: number;
  total: number;
}

export interface Ranking {
  meta: {
    indicator: string;
    unit: string;
    decimals: number;
    direction: string;
    level: Level;
    period: number;
    order: string;
  };
  items: RankItem[];
}

export interface AreaMetric {
  code: string;
  name_th: string;
  unit: string;
  decimals: number;
  direction: string;
  value: number;
  rank: number;
  of: number;
  year_be: number;
}

export interface AreaDetail {
  area: {
    area_code: string;
    level: Level;
    name_th: string;
    area_type: string;
    parent_code: string | null;
    parent_name: string | null;
  };
  metrics: AreaMetric[];
  children: number;
}
